import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStores } from '../../../stores';
import {
  Card,
  Descriptions,
  Button,
  Tag,
  Steps,
  Space,
  Modal,
  Input,
  message,
  Spin,
  Divider
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import './Appointments.scss';

const { Step } = Steps;
const { TextArea } = Input;

const STATUS_COLORS = {
  PENDING: 'orange',
  CONFIRMED: 'blue',
  COMPLETED: 'green',
  CANCELLED: 'red'
};

const PartnerAppointmentDetail = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { partnerAppointmentStore } = useStores();
  const { currentAppointment, loading } = partnerAppointmentStore;

  const [modalVisible, setModalVisible] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [partnerNotes, setPartnerNotes] = useState('');

  useEffect(() => {
    partnerAppointmentStore.fetchAppointmentById(id);
  }, [id, partnerAppointmentStore]);

  useEffect(() => {
    if (currentAppointment) {
      setPartnerNotes(currentAppointment.partner_notes || '');
    }
  }, [currentAppointment]);

  const getStatusStep = (status) => {
    switch (status) {
      case 'PENDING':
        return 0;
      case 'CONFIRMED':
        return 1;
      case 'COMPLETED':
        return 2;
      case 'CANCELLED':
        return 3;
      default:
        return 0;
    }
  };

  const handleStatusUpdate = (newStatus) => {
    setActionType(newStatus);
    setModalVisible(true);
  };

  const confirmStatusUpdate = async () => {
    const result = await partnerAppointmentStore.updateAppointmentStatus(
      id,
      actionType,
      partnerNotes
    );

    if (result.success) {
      message.success(t('partner.appointments.messages.statusUpdated'));
      setModalVisible(false);
      setActionType(null);
    } else {
      message.error(result.error);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <Spin size="large" />
      </div>
    );
  }

  if (!currentAppointment) {
    return (
      <div className="loading-container">
        <p>{t('partner.appointments.notFound')}</p>
      </div>
    );
  }

  const canConfirm = currentAppointment.status === 'PENDING';
  const canComplete = currentAppointment.status === 'CONFIRMED';
  const canCancel = !['COMPLETED', 'CANCELLED'].includes(currentAppointment.status);

  const getStatusText = (status) => {
    const statusMap = {
      PENDING: t('partner.appointments.status.pending'),
      CONFIRMED: t('partner.appointments.status.confirmed'),
      COMPLETED: t('partner.appointments.status.completed'),
      CANCELLED: t('partner.appointments.status.cancelled')
    };
    return statusMap[status] || status;
  };

  return (
    <div className="partner-appointment-detail">
      <div className="page-header">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/partner/appointments')}
        >
          {t('partner.appointments.backToAppointments')}
        </Button>
        <h1>{t('partner.appointments.appointmentNumber', { id: currentAppointment.id })}</h1>
      </div>

      <Card className="status-card">
        <Steps current={getStatusStep(currentAppointment.status)}>
          <Step title={t('partner.appointments.status.pending')} icon={<ClockCircleOutlined />} />
          <Step title={t('partner.appointments.status.confirmed')} icon={<CheckCircleOutlined />} />
          <Step title={t('partner.appointments.status.completed')} icon={<CheckCircleOutlined />} />
        </Steps>

        <div style={{ marginTop: 24, textAlign: 'center' }}>
          <Tag color={STATUS_COLORS[currentAppointment.status]} style={{ fontSize: 16, padding: '8px 16px' }}>
            {getStatusText(currentAppointment.status)}
          </Tag>
        </div>
      </Card>

      <Card title={t('partner.appointments.details.serviceDetails')}>
        <Descriptions bordered column={1}>
          <Descriptions.Item label={t('common.service')}>
            <Tag color="blue">{currentAppointment.service_name}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t('partner.appointments.details.date')}>
            <CalendarOutlined /> {dayjs(currentAppointment.appointment_date).format('MMMM D, YYYY (dddd)')}
          </Descriptions.Item>
          <Descriptions.Item label={t('partner.appointments.details.time')}>
            <ClockCircleOutlined /> {currentAppointment.start_time} ({currentAppointment.duration} {t('common.minutes')})
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title={t('partner.appointments.details.customerInfo')}>
        <Descriptions bordered column={1}>
          <Descriptions.Item label={t('partner.appointments.details.name')}>
            <UserOutlined /> {currentAppointment.customer_first_name} {currentAppointment.customer_last_name}
          </Descriptions.Item>
          <Descriptions.Item label={t('partner.appointments.details.email')}>
            <MailOutlined /> {currentAppointment.customer_email}
          </Descriptions.Item>
          {currentAppointment.customer_phone && (
            <Descriptions.Item label={t('partner.appointments.details.phone')}>
              <PhoneOutlined /> {currentAppointment.customer_phone}
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      {currentAppointment.pet_name && (
        <Card title={t('partner.appointments.details.animalInfo')}>
          <Descriptions bordered column={1}>
            <Descriptions.Item label={t('partner.appointments.details.animalName')}>
              {currentAppointment.pet_name}
            </Descriptions.Item>
            {currentAppointment.pet_breed && (
              <Descriptions.Item label={t('partner.appointments.details.breed')}>
                {currentAppointment.pet_breed}
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>
      )}

      <Card title={t('partner.appointments.details.notes')}>
        <Descriptions bordered column={1}>
          <Descriptions.Item label={t('partner.appointments.details.customerNotes')}>
            {currentAppointment.customer_notes || <span style={{ color: '#ccc' }}>{t('partner.appointments.details.noNotes')}</span>}
          </Descriptions.Item>
          <Descriptions.Item label={t('partner.appointments.details.partnerNotes')}>
            <TextArea
              rows={4}
              value={partnerNotes}
              onChange={(e) => setPartnerNotes(e.target.value)}
              placeholder={t('partner.appointments.details.addNotesPlaceholder')}
            />
            <Button
              type="primary"
              style={{ marginTop: 8 }}
              onClick={() => handleStatusUpdate(currentAppointment.status)}
            >
              {t('partner.appointments.details.saveNotes')}
            </Button>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title={t('common.actions')}>
        <Space>
          {canConfirm && (
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={() => handleStatusUpdate('CONFIRMED')}
            >
              {t('partner.appointments.actions.confirm')}
            </Button>
          )}

          {canComplete && (
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={() => handleStatusUpdate('COMPLETED')}
            >
              {t('partner.appointments.actions.markCompleted')}
            </Button>
          )}

          {canCancel && (
            <Button
              danger
              icon={<CloseCircleOutlined />}
              onClick={() => handleStatusUpdate('CANCELLED')}
            >
              {t('partner.appointments.actions.cancel')}
            </Button>
          )}
        </Space>
      </Card>

      <Modal
        title={t('partner.appointments.modal.title', { action: getStatusText(actionType) })}
        open={modalVisible}
        onOk={confirmStatusUpdate}
        onCancel={() => {
          setModalVisible(false);
          setActionType(null);
        }}
        okText={t('common.confirm')}
        cancelText={t('common.cancel')}
      >
        <p>{t('partner.appointments.modal.confirmMessage', { action: actionType?.toLowerCase() })}</p>
        {actionType && (
          <>
            <Divider />
            <TextArea
              rows={4}
              value={partnerNotes}
              onChange={(e) => setPartnerNotes(e.target.value)}
              placeholder={t('partner.appointments.details.optionalNotesPlaceholder')}
            />
          </>
        )}
      </Modal>
    </div>
  );
});

export default PartnerAppointmentDetail;
